<template>
    <MenuItemContainer :noHoverEffects="true"
                       class="d-flex justify-content-center">
        <div ref="cryptoTermsContainerRef"
             class="crypto-terms">
            <h4 class="text-center mb-0 mt-2 mb-2">
                {{ title }}
            </h4>
            <div class="first-row">
                <div v-for="(cryptoTerm, index) in data.slice(0, 3)"
                     :key="index"
                     class="term-container">
                    <a id="crypto-term"
                       class="item"
                       :href="cryptoTerm.url">
                        <p class="mb-0">{{ cryptoTerm.title }}</p>
                    </a>
                </div>
            </div>
            <div class="second-row">
                <div v-for="(cryptoTerm, index) in data.slice(3, 6)"
                     :key="index"
                     class="term-container">
                    <a id="crypto-term"
                       class="item"
                       :href="cryptoTerm.url">
                        <p class="mb-0">{{ cryptoTerm.title }}</p>
                    </a>
                </div>
            </div>
            <div class="third-row">
                <div v-for="(cryptoTerm, index) in data.slice(6, 9)"
                     :key="index"
                     class="term-container">
                    <a id="crypto-term"
                       class="item"
                       :href="cryptoTerm.url">
                        <p class="mb-0">{{ cryptoTerm.title }}</p>
                    </a>
                </div>
            </div>
        </div>
    </MenuItemContainer>
</template>

<script setup lang="ts">
    import { onMounted, onUnmounted, ref } from 'vue';
    import MenuItemContainer from '../components/MenuItemContainer.vue';
    import { INavigationDiagram } from '../types/menuTypes';

    defineProps<INavigationDiagram>();

    const cryptoTermsContainerRef = ref<HTMLDivElement | null>(null);
    const timeoutId = ref<number>(null);
    const animationFrameId = ref<number>(null);

    const randomizeCryptoTerms = () => {
        const cryptoTermsElements: NodeListOf<HTMLAnchorElement> = document.querySelectorAll('#crypto-term');

        const TIMEOUTS = [2000, 2300, 2500];
        const TIMEOUT = TIMEOUTS[Math.floor(Math.random() * 3)];

        if (cryptoTermsContainerRef.value && cryptoTermsElements) {
            cryptoTermsElements.forEach((bubble, index) => {
                const RANDOM_NUMBER = Math.floor(Math.random() * 2 + 1) * 10;
                const cryptoTermsContainerRect = cryptoTermsContainerRef.value.getBoundingClientRect();
                const bubbleRect = bubble.getBoundingClientRect();
                const ANIMATION_COLOR_LIGHT = '__animationColorLight';
                const ANIMATION_COLOR_DARK = '__animationColorDark';
                let stepX = 0;
                let stepY = 0;

                bubble.style.setProperty(`--bubble${index + 1}PulseAnimation`, `bubble-pulse 5s linear infinite ${index * (Math.floor(Math.random() + 2))}s`);

                if ((index + 1) % 2 === 0) {
                    const ROYAL_PURPLE = '#6a2d94';
                    const FOG = '#dbd0f1';

                    bubble.style.setProperty(ANIMATION_COLOR_LIGHT, ROYAL_PURPLE);
                    bubble.style.setProperty(ANIMATION_COLOR_DARK, FOG);
                } else {
                    const COUNTY_GREEN = '#0f5238';
                    const VISTA_BLUE = '#99dac1';

                    bubble.style.setProperty(ANIMATION_COLOR_LIGHT, COUNTY_GREEN);
                    bubble.style.setProperty(ANIMATION_COLOR_DARK, VISTA_BLUE);
                }

                if (bubbleRect.right > cryptoTermsContainerRect.right - 20) {
                    stepX -= 20;
                    bubble.style.transform = `translateX(${RANDOM_NUMBER + stepX}px)`;
                } else if (bubbleRect.left < cryptoTermsContainerRect.left + 20) {
                    stepX += 10;
                    bubble.style.transform = `translateX(${RANDOM_NUMBER + stepX}px)`;
                } else if (bubbleRect.top < cryptoTermsContainerRect.top + 40) {
                    stepY += 10;
                    bubble.style.transform = `translateY(${RANDOM_NUMBER + stepY}px)`;
                } else if (bubbleRect.bottom > cryptoTermsContainerRect.bottom - 50) {
                    stepY -= 20;
                    bubble.style.transform = `translateY(${RANDOM_NUMBER + stepY}px)`;
                } else {
                    bubble.style.transform = `rotate(60deg) translate(-${RANDOM_NUMBER}px, -${RANDOM_NUMBER}px) rotate(-60deg)`;
                }
            });
        }

        timeoutId.value = window.setTimeout(() => {
            animationFrameId.value = requestAnimationFrame(randomizeCryptoTerms);
        }, TIMEOUT);
    };

    onMounted(() => {
        requestAnimationFrame(randomizeCryptoTerms);
    });

    onUnmounted(() => {
        clearTimeout(timeoutId.value);
        cancelAnimationFrame(animationFrameId.value);
    });
</script>

<style scoped lang="scss">
@use "@/sass/partials/z-index" as ZIndex;
@use "@/sass/partials/variables" as legacyVariables;

.crypto-terms {
    position: relative;
    display: flex;
    overflow: hidden;
    width: 400px;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding-top: 10px;
    padding-bottom: 30px;
    background:
        radial-gradient(
            legacyVariables.$border 0%,
            legacyVariables.$whisper-light 32%,
            rgba(9, 37, 64, 0.03) 32%,
            rgba(9, 37, 64, 0.002) 37%,
            rgba(229, 229, 229, 0.6) 37%,
            rgba(229, 229, 229, 0.6) 40%,
            rgba(9, 37, 64, 0.005) 40%,
            rgba(9, 37, 64, 0.0005) 45%,
            rgba(229, 229, 229, 0.4) 45%,
            rgba(229, 229, 229, 0.4) 48%,
            rgba(9, 37, 64, 0.0005) 48%,
            transparent 52%,
            transparent 100%,
        );

    .first-row,
    .second-row,
    .third-row {
        display: flex;
        margin: 0 16px;

        .term-container {
            .item {
                &::before {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    content: "";
                    opacity: 0;
                }
            }
        }
    }

    .first-row,
    .third-row {
        .term-container {
            &:nth-of-type(1) .item,
            &:nth-of-type(3) .item {
                border: 1px solid legacyVariables.$green;
                background-color: rgba(legacyVariables.$vista-blue, 0.5);

                &::before {
                    background-color: rgba(legacyVariables.$vista-blue, 0.5);
                }
            }
        }
    }

    .second-row {
        .term-container {
            &:nth-of-type(2) .item {
                border: 1px solid green;
                background-color: rgba(legacyVariables.$vista-blue, 0.5);

                &::before {
                    background-color: rgba(legacyVariables.$vista-blue, 0.5);
                }
            }
        }
    }

    .first-row {
        .term-container {
            &:nth-of-type(1) {
                top: 30px;
                left: -10px;

                .item {
                    &::before {
                        animation: var(--bubble1PulseAnimation);
                    }
                }
            }

            &:nth-of-type(2) {
                top: 10px;
                left: 0;

                .item {
                    &::before {
                        animation: var(--bubble2PulseAnimation);
                    }
                }
            }

            &:nth-of-type(3) {
                top: 30px;
                left: 10px;

                .item {
                    &::before {
                        animation: var(--bubble3PulseAnimation);
                    }
                }
            }
        }
    }

    .second-row {
        margin-top: -12px;
        margin-bottom: 12px;

        .term-container {
            &:nth-of-type(1) {
                top: 40px;
                left: -10px;

                .item {
                    &::before {
                        animation: var(--bubble4PulseAnimation);
                    }
                }
            }

            &:nth-of-type(2) {
                top: 40px;

                .item {
                    &::before {
                        animation: var(--bubble5PulseAnimation);
                    }
                }
            }

            &:nth-of-type(3) {
                top: 40px;
                left: 10px;

                .item {
                    &::before {
                        animation: var(--bubble6PulseAnimation);
                    }
                }
            }
        }
    }

    .third-row {
        .term-container {
            &:nth-of-type(1) {
                top: 0;

                .item {
                    &::before {
                        animation: var(--bubble7PulseAnimation);
                    }
                }
            }

            &:nth-of-type(2) {
                top: 20px;
                left: 0;

                .item {
                    &::before {
                        animation: var(--bubble8PulseAnimation);
                    }
                }
            }

            &:nth-of-type(3) {
                top: 0;
                right: -10px;

                .item {
                    &::before {
                        animation: var(--bubble9PulseAnimation);
                    }
                }
            }
        }
    }

    @keyframes bubble-pulse {
        0% {
            background-color: var(--animationColorLight);
            opacity: 0;
            transform: scale(0.3);
        }

        50% {
            background-color: var(-animationColorDark);
            opacity: 0.3;
        }

        70% {
            background-color: var(--animationColorDark);
            opacity: 0.09;
        }

        100% {
            background-color: var(--animationColorLight);
            opacity: 0;
            transform: scale(1);
        }
    }

    .first-row,
    .second-row,
    .third-row {
        .term-container {
            position: relative;
            width: 100%;
            height: auto;

            a {
                position: relative;
                display: flex;
                overflow: hidden;
                width: fit-content;
                height: auto;
                align-items: center;
                justify-content: center;
                padding: 10px;
                border: 1px solid legacyVariables.$purple;
                border-radius: 50%;
                margin: 2px;
                aspect-ratio: 1;
                background-color: rgba(legacyVariables.$fog, 0.5);
                opacity: 1;
                text-overflow: ellipsis;
                transform-origin: right;
                transition: transform 3500ms, background-color 3500ms;

                p {
                    z-index: ZIndex.$header-p-z-index;
                    font-size: 11px;
                    font-weight: 700;
                    line-height: 18px;
                    opacity: 1;
                    text-align: center;
                }

                &:hover {
                    text-decoration: underline;
                }

                &::before {
                    background-color: rgba(legacyVariables.$fog, 0.5);
                }
            }
        }
    }
}

.dark-theme {
    .crypto-terms {
        background:
            radial-gradient(
                #44056c 0%,
                #3b035e 32%,
                rgba(9, 37, 64, 0.03) 32%,
                rgba(9, 37, 64, 0.002) 37%,
                rgba(59, 3, 94, 0.8) 37%,
                rgba(59, 3, 94, 0.8) 40%,
                rgba(9, 37, 64, 0.005) 40%,
                rgba(9, 37, 64, 0.0005) 45%,
                rgba(59, 3, 94, 0.6) 45%,
                rgba(59, 3, 94, 0.6) 48%,
                rgba(9, 37, 64, 0.0005) 48%,
                transparent 52%,
                transparent 100%
            );

        .first-row,
        .second-row,
        .third-row {
            .item {
                border: 1px solid purple;
                background-color: rgba(legacyVariables.$royal-purple, 0.5);

                &::before {
                    background-color: rgba(legacyVariables.$royal-purple, 0.5);
                }
            }
        }

        @keyframes bubble-pulse {
            0% {
                background-color: var(--animationColorDark);
                opacity: 0;
                transform: scale(0.3);
            }

            50% {
                background-color: var(-animationColorLight);
                opacity: 0.3;
            }

            70% {
                background-color: var(--animationColorLight);
                opacity: 0.09;
            }

            100% {
                background-color: var(--animationColorDark);
                opacity: 0;
                transform: scale(3);
            }
        }

        .first-row,
        .third-row {
            .term-container {
                &:nth-of-type(1) .item,
                &:nth-of-type(3) .item {
                    border: 1px solid legacyVariables.$green;
                    background-color: rgba(legacyVariables.$county-green, 0.5);

                    &::before {
                        background-color: rgba(legacyVariables.$county-green, 0.5);
                    }
                }
            }
        }

        .second-row {
            .term-container {
                &:nth-of-type(2) .item {
                    border: 1px solid legacyVariables.$green;
                    background-color: rgba(legacyVariables.$county-green, 0.5);

                    &::before {
                        background-color: rgba(legacyVariables.$county-green, 0.5);
                    }
                }
            }
        }
    }
}
</style>
